import Box from "@mui/material/Box";
import {
    ACard,
    ADataGrid,
    ADataGridColumn,
    ADataGridFilter,
    ASectionTitle,
    PagedSearchParams
} from "@atiautomacao/ati-ui-library";
import React, {useCallback, useEffect, useState} from "react";
import {
    EquipmentSearchReactQuery, InhibitEquipmentRequest,
    InhibitEquipmentsReactQuery,
} from "../../Settings/Equipment/EquipmentReactQuery";
import {extractFiltersFromColumns} from "../../../Utils/DataUitils";
import {EquipmentProps} from "../../../Shared/Types/Equipment";
import {Button, Switch} from "@mui/material";
import {Pill} from "./PIll";
import Typography from "@mui/material/Typography";
import {
    NotificationsActiveOutlined,
    HourglassEmptyOutlined,
} from "@mui/icons-material";
import {useQueryClient} from "react-query";
import {useSnackbar} from "notistack";
import EquipmentDialog from "./components/EquipmentDialog";
import {hasPermission} from "../../../Shared/Auth/AuthenticationUtil";
import {AUTHORITIES} from "../../../Config/Constants";
import {useAppSelector} from "../../../Config/Hooks";


const initColumns = [
    {
        name: 'name',
        label: 'Equipamento',
        align: 'left',
        visibility: true,
        minWidth: 100,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'name', condition: 'contains', value: '', sort: 'asc'}

    },
    {
        name: 'powerStationName',
        label: 'Usina',
        align: 'left',
        visibility: true,
        minWidth: 100,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'powerStationName', condition: 'contains', value: '', sort: 'none'}

    },
    {
        name: 'skidName',
        label: 'Skid',
        align: 'left',
        visibility: true,
        minWidth: 100,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'skidName', condition: 'contains', value: '', sort: 'none'}

    },
    {
        name: 'etsStatus',
        label: 'Situação',
        align: 'left',
        visibility: true,
        minWidth: 100,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'etsStatus', condition: 'contains', value: '', sort: 'none'}

    },
    {
        name: 'actions',
        label: 'Ações',
        align: 'center',
        visibility: true,
        minWidth: 80,
        breakpoints: { xs: true, sm: true, md: true, lg: true, xl: true },
        filter: {name: 'actions', condition: 'contains', value: '', sort: ''}
    }
] as ADataGridColumn [];

export interface RowEquipment {
    id: number;
    name: string;
    // actions column will render any React node (we'll put a Switch here)
    actions?: React.ReactNode;
    etsStatus?: React.ReactNode;
    // textual status (used for filtering / toggling)
    etsStatusText?: string;
    equipmentClassName?: string;
    powerStationName?: string;
    skidName?: string;
}

export function EquipmentsPage() {
    const [pagedSearchParams, setPagedSearchParams] = useState(new PagedSearchParams(extractFiltersFromColumns(initColumns), 0, 150));
    const [rows, setRows] = useState<Array<RowEquipment>>([]);
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const [isClear, setIsClear] = useState(false)
    const MAX_EQUIPMENTS = 10

    const handleOnFiltersChange = useCallback((dataGridFilters: Array<ADataGridFilter>, page: number, pageSize: number) => {
        let newPageParams: PagedSearchParams = new PagedSearchParams(dataGridFilters, page, pageSize);
        setPagedSearchParams(
            newPageParams
        );
    }, []);

    const {data, isSuccess} = EquipmentSearchReactQuery(pagedSearchParams);
    const [totalOfRecords, setTotalOfRecords] = useState(0);
    const mutationInhibit = InhibitEquipmentsReactQuery();
    const [selectedIds, setSelectedIds] = useState<Array<number>>([])
    const [dialogProps, setDialogProps] = useState({open: false, mode: ""})
    const account = useAppSelector(state => state.authentication.account);
    const isAuthorizedToInhibitOrActivateEquipment = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.TOGGLE_EQUIPMENT_INHIBITION
    ]);

    // toggle handler: flips Habilitado <-> Inibido locally; later you can hook a mutation here
    const handleToggle = useCallback((id: number, isEnabled: boolean) => {
        if(!isAuthorizedToInhibitOrActivateEquipment){
            enqueueSnackbar(`Você não tem permissão para habilitar ou inibir equipamentos!`, { variant: "error", autoHideDuration: 2000 });
            return
        }
        const action = isEnabled ? "ACTIVATE" : "INHIBIT"
        setRows(prev => prev.map(r => {
            if (r.id !== id) return r;
            const current = r.etsStatusText ?? '';
            const newEnabled = current !== 'Habilitado';
            const newStatus = newEnabled ? 'Habilitado' : 'Inibido';
            let tone: any = 'neutral';
            if (newStatus === 'Inibido') tone = 'openOrange';
            else if (newStatus === 'Habilitado') tone = 'doneGreen';
            return {
                ...r,
                etsStatusText: newStatus,
                etsStatus: <Pill label={newStatus} tone={tone} />,
                actions: (
                    <Switch
                        checked={newEnabled}
                        onChange={() => handleToggle(id, newEnabled)}
                        color="primary"
                        inputProps={{ 'aria-label': `toggle-equipment-${id}` }}
                    />
                )
            };
        }));
        actionTable(action, [id])
    }, [setRows]);

    useEffect(() => {
        if (isSuccess) {
            setTotalOfRecords(data?.totalOfRecords);
            if (data?.data) {
                let rowsEquipment = data.data.map((equipment: EquipmentProps) => {
                    const status = equipment.etsStatus ?? "";
                    let tone: any = 'neutral';
                    if (status === 'Inibido') tone = 'openOrange';
                    else if (status === 'Habilitado') tone = 'doneGreen';

                    const isEnabled = status === 'Habilitado';

                    return {
                        id: equipment.id,
                        name: equipment.name,
                        etsStatus: <Pill label={status} tone={tone} />,
                        etsStatusText: status,
                        equipmentClassName: equipment.equipmentClass.name,
                        powerStationName: equipment.powerStation.name,
                        skidName: equipment.skid?.name ?? "",
                        // put the Switch into the actions field (ADataGrid will render it)
                        actions: (
                            <Switch
                                checked={isEnabled}
                                onChange={() => equipment.id != null && handleToggle(equipment.id, !isEnabled)}
                                color="primary"
                                inputProps={{ 'aria-label': `toggle-equipment-${equipment.id}` }}
                            />
                        )
                    };
                });
                setRows(rowsEquipment);
                setIsClear(false)
            }
        }
    }, [isSuccess, data?.data, data?.totalOfRecords, handleToggle]);

    const rowSelect = (data:any) => {
        setSelectedIds(data.map((item:any) => item.id))
    }

    const handleDialog = (action:string) => {
        if(!isAuthorizedToInhibitOrActivateEquipment){
            enqueueSnackbar(`Você não tem permissão para habilitar ou inibir equipamentos!`, { variant: "error", autoHideDuration: 2000 });
            return
        }
        setDialogProps({
           open: true,
           mode: action
       })
    }

    const actionTable = async (action: string, equipmentIds: number[]) => {
        const requestDTO: InhibitEquipmentRequest = {
            equipmentIds: equipmentIds,
            action: action
        };

        const actionMessage = action === 'ACTIVATE' ? 'habilitados' : 'inibidos'
        const actionErrorMessage = action === 'ACTIVATE' ? 'habilitar' : 'inibir'
        return mutationInhibit.mutateAsync(requestDTO, {
            onSuccess: () => {
                enqueueSnackbar(`Equipamentos ${actionMessage} com sucesso!`, { variant: "success", autoHideDuration: 2000 });
                setSelectedIds([])
                setIsClear(true)
            },
            onError: (error:any) => {
                enqueueSnackbar(`Erro ao ${actionErrorMessage} equipamentos: ${error.message}`, { variant: "error", autoHideDuration: 2000 });
            },
            onSettled:  () => {
                queryClient.invalidateQueries({
                    queryKey: ["equipment","search"],
                    exact: false,
                    refetchInactive: true
                })
            }

        });
    }

    const formatEquipmentRowStatus = (id: number, isEnabled: boolean, handleToggle: (id: number, status: boolean) => void) => {
        const statusText = isEnabled ? 'Habilitado' : 'Inibido';
        const tone = isEnabled ? 'doneGreen' : 'openOrange';

        return {
            etsStatusText: statusText,
            etsStatus: <Pill label={statusText} tone={tone} />,
            actions: (
                <Switch
                    checked={isEnabled}
                    onChange={() => handleToggle(id, !isEnabled)}
                    color="primary"
                    inputProps={{ 'aria-label': `toggle-equipment-${id}` }}
                />
            )
        };
    };

    const onConfirm = async (action: string) => {
        const isActivate = action === 'ACTIVATE';
        setRows(prevState => prevState.map(row => {
            if (!selectedIds.includes(row.id)) return row;
            return {
                ...row,
                ...formatEquipmentRowStatus(row.id, isActivate, handleToggle)
            };
        }));

        await actionTable(action, selectedIds);
        setDialogProps(prev => ({ ...prev, open: false }));
    }

    const handleMaxSelected = (isMax: boolean) => {
        if(isMax) {
            enqueueSnackbar(`Limite de ${MAX_EQUIPMENTS} equipamentos atingido!`, { variant: "warning", autoHideDuration: 2000 });
        }

    }

    return (
        <>

            <Box sx={{flex: 1, minWidth: 0, mb: 3, marginLeft: 3}}>
                <ASectionTitle
                    titleLabel="Inibe/Habilita Equipamentos"
                    subTitleLabel="Gerencie, habilite e iniba equipamentos"
                />
            </Box>

            <Box sx={{marginLeft: 3}}>
                <ACard styleProps={{
                    headerStyle: {height: 0, padding: 0},
                    cardStyle: {borderRadius: "0 0 8px 8px"},
                    contentStyle: {padding: 0}
                }}>
                    {/* 🔹 HEADER SUPERIOR DO CARD */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            px: 3,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            height: 64
                        }}
                    >
                        {/* Lado esquerdo */}
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 500,
                                color: selectedIds.length > 0 ? "#000000DE" : "#6B7280",
                                fontSize: "14px",
                            }}
                        >
                            {selectedIds.length} itens selecionados
                        </Typography>

                        {/* Lado direito - Botões */}
                        <Box sx={{ display: "flex", gap: 2 }}>
                            <Button
                                disabled={selectedIds.length === 0 || mutationInhibit.isLoading}
                                variant="text"
                                onClick={() => {handleDialog("ACTIVATE")}}
                                endIcon={<NotificationsActiveOutlined />}
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "14px",
                                    color: "#000000DE"
                                }}
                            >
                                HABILITAR
                            </Button>

                            <Button
                                variant="text"
                                disabled={selectedIds.length === 0 || mutationInhibit.isLoading}
                                onClick={() => {handleDialog("INHIBIT")}}
                                endIcon={<HourglassEmptyOutlined />}
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "14px",
                                    color: "#000000DE"
                                }}
                            >
                                INIBIR
                            </Button>
                        </Box>
                    </Box>
                    <ADataGrid
                        ariaLabel={"settings-grid-equipment"}
                        hideFilters={false}
                        showFilterOptions={false}
                        hideSelection={false}
                        onRowSelect={rowSelect}
                        maxRowSelected={MAX_EQUIPMENTS}
                        onMaxRowSelected={handleMaxSelected}
                        columns={initColumns}
                        rows={rows}
                        page={pagedSearchParams.page}
                        rowsPerPage={pagedSearchParams.size}
                        loading={false}
                        totalOfRecords={totalOfRecords}
                        onFiltersChange={handleOnFiltersChange}
                        tableContainerStyle={{ borderRadius: 0}}
                        onClearSelectedTempRows={isClear}
                    />
                </ACard>
                <EquipmentDialog
                    open={dialogProps.open}
                    mode={dialogProps.mode}
                    onClose={() => {setDialogProps({mode: dialogProps.mode, open: false})}}
                    onConfirm={(action:string) => {onConfirm(action)}}
                    isLoading={mutationInhibit.isLoading}
                />
            </Box>
        </>

    )
}